/**
 * Generic per-user localization engine — the reactive plumbing so a Lit
 * component can read `this.t(key)` and re-render when the UI language
 * flips. Carries zero app strings AND zero app language decisions: the
 * catalog (message copy) and the language-key set it's indexed by are both
 * generic type parameters the consumer injects at boot. This module knows
 * only the *shape* of a catalog, never its content or which languages exist.
 *
 * Two axes stay distinct:
 *  - UI language (`keyof C & string`, inferred from the consumer's own
 *    catalog) selects which catalog entry `t()` reads. Backed by a schmancy
 *    `state()` singleton so the reactive plumbing is schmancy's own
 *    primitive, not a bespoke listener Set.
 *  - Formatting locale (`<schmancy-theme locale>`, a region-complete BCP-47
 *    tag) drives `Intl`. `setLocale` maps the UI language onto it through
 *    the `formattingLocales` map the consumer supplies — never a hardcoded
 *    table schmancy owns — so the UI-language axis never coarsens the
 *    theme's formatting axis to a bare language tag.
 *
 * The UI language is also the language the document is written in, so the
 * engine names it on `<html lang>` (WCAG 3.1.1): a screen reader picks its
 * pronunciation rules from that attribute, and an app that switches to German
 * while the page still says `en` is read aloud with English rules.
 */
import type { LitElement } from 'lit'
import { combineLatest, identity, tap, type Observable } from 'rxjs'
import { interpolate, type Constructor } from '@schmancy/mixins'
import { bindState, state } from '@schmancy/state'
import { theme as themeService } from '@schmancy/theme/theme.service'

/** Dotted-path union of every string-leaf key in a catalog entry's shape. */
export type NestedKeyOf<T> = {
	[K in keyof T & string]: T[K] extends string ? `${K}` : T[K] extends object ? `${K}.${NestedKeyOf<T[K]>}` : never
}[keyof T & string]

/** Instance API `LocalizeMixin` adds to its base class. */
export interface ILocalizeMixin<TKeys extends string, TLang extends string> {
	/** Look up `key` in the injected catalog for the current UI language,
	 *  substituting `{{param}}` placeholders. Returns `key` itself on a miss. */
	t(key: TKeys, params?: Record<string, string | number>): string
	/** Current UI language. Reactive — reading it in `render()` is safe;
	 *  the mixin re-renders on every change. Named `uiLanguage` (not
	 *  `locale`) so it never shadows `SchmancyElement`/`BaseElement`'s own
	 *  `locale` getter, which reads the theme's BCP-47 *formatting* tag —
	 *  the two axes stay distinct properties, not one name reused for both. */
	readonly uiLanguage: TLang
}

export interface Localization<TKeys extends string, TLang extends string> {
	LocalizeMixin: <T extends Constructor<LitElement>>(superClass: T) => Constructor<ILocalizeMixin<TKeys, TLang>> & T
	/** Updates the reactive UI-language state. The theme's formatting locale
	 *  follows reactively (see `createLocalization`) — applied immediately
	 *  when a `<schmancy-theme>` is already attached, and applied on attach
	 *  when this fires before the shell has mounted. */
	setLocale(lang: TLang): void
	locale$: Observable<TLang>
}

export interface LocalizationOptions<TLang extends string> {
	/** The UI language before any `setLocale`. Required rather than read off
	 *  the catalog: which language an app opens in is the app's decision, and
	 *  a catalog's key order is not a place anyone makes it. */
	defaultLocale: TLang
}

/**
 * Creates the localization engine for a catalog keyed by the consumer's own
 * language set, plus that same key set's mapping onto region-complete
 * BCP-47 formatting locales (e.g. `{ de: 'de-DE', en: 'en-GB' }`). Call once
 * at app boot — `LocalizeMixin`, `setLocale`, and `locale$` all share the
 * one schmancy `state()` singleton, so every element the mixin touches and
 * every later call to `setLocale` stay in sync.
 *
 * Every catalog key is written verbatim onto `<html lang>`, so each one is a
 * BCP-47 language tag (`de`, `en`, `pt-BR`).
 */
export function createLocalization<C extends Record<string, object>>(
	catalog: C,
	formattingLocales: Record<keyof C & string, string>,
	options: LocalizationOptions<keyof C & string>,
) {
	type Lang = keyof C & string
	type Keys = NestedKeyOf<C[Lang]>

	// Backed by state<string>(), not state<Lang>(): Lang is still an abstract
	// type parameter here (bound only by `keyof C & string`), and the state
	// module's write-API dispatch (Kind<T>) can't collapse a generic,
	// unresolved T to its 'primitive' branch — only a concrete T. `Lang
	// extends string` makes every write into `.set()` an ordinary widening
	// assignment; every read back out is narrowed with `as Lang` at the
	// point of use, justified by the invariant that `setLocale` is the sole
	// writer and only ever passes a `Lang`.
	// keepOnClear: a UI-language slot is a device preference, not per-identity
	// data — clearStates() (fired on every identity switch, a different uid
	// signing in on the same device) must leave it untouched, or the app's
	// own device-derived boot value (localStorage/navigator) gets wiped and
	// the UI flashes to the default language until the new user's own
	// per-user reconcile overrides it.
	const localeState = state<string>('i18n/locale').memory(options.defaultLocale, { keepOnClear: true })

	// A single reactive pipeline carries the UI language across both axes it
	// crosses. `<html lang>` is written from the locale source itself, so it
	// lands on the first emission and every switch without waiting on a theme.
	// The theme's formatting locale follows on BOTH of its triggers — a
	// setLocale call, and a <schmancy-theme> attaching — so a setLocale fired
	// before the shell has mounted still lands once the theme attaches, rather
	// than silently no-op'ing. No document (SSR, a worker) leaves the attribute
	// alone rather than throwing at import.
	combineLatest([
		localeState.$.pipe(
			typeof document === 'undefined'
				? identity
				: tap(lang => {
						document.documentElement.lang = lang
					}),
		),
		themeService.themeComponent$,
	]).subscribe(([lang, component]) => {
		if (component) component.locale = formattingLocales[lang as Lang]
	})

	function setLocale(lang: Lang): void {
		localeState.set(lang)
	}

	function LocalizeMixin<T extends Constructor<LitElement>>(
		superClass: T,
	): Constructor<ILocalizeMixin<Keys, Lang>> & T {
		class LocalizedElement extends superClass {
			#bound = bindState(this, localeState)

			get uiLanguage(): Lang {
				return this.#bound.value as Lang
			}

			t(key: Keys, params?: Record<string, string | number>): string {
				const value = key
					.split('.')
					.reduce<unknown>(
						(node, segment) =>
							node && typeof node === 'object' ? (node as Record<string, unknown>)[segment] : undefined,
						catalog[this.#bound.value as Lang],
					)
				if (typeof value !== 'string') return key
				// Shared with `componentText` (`@schmancy/mixins`), so a consumer's own
				// catalog and the library's built-in wording answer to one substitution
				// syntax rather than two that can drift apart.
				return interpolate(value, params)
			}
		}
		return LocalizedElement as Constructor<ILocalizeMixin<Keys, Lang>> & T
	}

	return {
		LocalizeMixin,
		setLocale,
		locale$: localeState.$ as Observable<Lang>,
	}
}
